import { Close } from '@mui/icons-material';
import { Box, Popover, PopoverProps, Stack, useMediaQuery, Theme } from '@mui/material';
import { formatBytes } from 'component/MarketPlace/OrderRequestDetail/utils';
import Typography from 'component/MarketPlace/Typography';
import React from 'react';
import { Attachment } from '../comments.model';
import { truncateFileName } from '../utils';
import ActionIconButton from './actionIconButton';
import {
  StyledCard,
  StyledCloseButton,
  StyledContainer,
  StyledFileInfo,
} from './attachmentPopover.style';
import FileIcon from './fileIcon';

interface AttachmentPopoverProps extends PopoverProps {
  attachments: Attachment[];
  onClose?: () => void;
  onDownloadAttachment?: (attachment: Attachment) => void;
  locale?: Record<string, any>;
}

const AttachmentPopover = (props: AttachmentPopoverProps) => {
  const { attachments, onDownloadAttachment, locale, ...popoverProps } = props;

  const isMdDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const renderDate = (attachment: Attachment) => {
    const date = attachment.updated_on || attachment.created_on;
    return date ? `${date} • ` : '';
  };

  return (
    <Popover
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      {...popoverProps}
    >
      <StyledContainer isMobile={isMdDown}>
        <StyledCloseButton icon={Close} onClick={props.onClose} />

        <Typography variant="h3" mb="32px">
          {locale?.attachments}
        </Typography>

        <Stack direction={{ xs: 'column', sm: "row" }} spacing="24px" sx={{ overflowX: { xs: "none", sm: "scroll" }}} overflow="auto">
          {attachments.map((attachment) => (
            <Box key={attachment.sys_id} position="relative">
              <ActionIconButton
                onDownload={() => onDownloadAttachment?.(attachment)}
                locale={locale}
              />

              <StyledCard>
                <FileIcon fileName={attachment.file_name} />
              </StyledCard>

              <StyledFileInfo>
                <FileIcon fileName={attachment.file_name} />

                <Box>
                  <Typography variant="body2" color="text.primary">
                    {truncateFileName(attachment.file_name, 20)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {renderDate(attachment)}
                    {formatBytes(Number(attachment.size))}
                  </Typography>
                </Box>
              </StyledFileInfo>
            </Box>
          ))}
        </Stack>
      </StyledContainer>
    </Popover>
  );
};

AttachmentPopover.defaultProps = {
  locale: {
    attachments: 'Attachments',
    download: 'Download',
  },
};

export default AttachmentPopover;
